import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { knowledgeBaseApi } from '../../services/knowledgeBaseApi';
import './AIExplainButton.css';

interface AIExplainButtonProps {
  configSection: any;
  sectionName?: string;
  buttonText?: string;
  className?: string;
}

const AIExplainButton: React.FC<AIExplainButtonProps> = ({
  configSection,
  sectionName,
  buttonText = '?',
  className = ''
}) => {
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleExplain = async (forceRefresh: boolean = false) => {
    if (explanation && !forceRefresh) {
      // If we already have an explanation and not forcing refresh, just show it
      setShowModal(true);
      return;
    }

    if (forceRefresh) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await knowledgeBaseApi.explainConfigWithAI(configSection, sectionName);
      
      if (result.success) {
        setExplanation(result.explanation);
        if (!showModal) {
          setShowModal(true);
        }
      } else {
        setError('Failed to get explanation');
      }
    } catch (err: any) {
      setError(err.message || 'Error getting explanation');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    handleExplain(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setError(null);
  };

  return (
    <>
      <button
        className={`ai-explain-button ${className}`}
        onClick={() => handleExplain(false)}
        disabled={loading}
        title="Get AI explanation"
      >
        {loading ? '...' : buttonText}
      </button>

      {showModal && (
        <div className="ai-explain-modal-overlay" onClick={closeModal}>
          <div className="ai-explain-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ai-explain-modal-header">
              <h3>{sectionName ? `Understanding: ${sectionName}` : 'AI Explanation'}</h3>
              <button 
                className="ai-explain-refresh" 
                onClick={handleRefresh}
                disabled={isRefreshing}
                title="Refresh explanation"
              >
                {isRefreshing ? '⟳' : '↻'}
              </button>
            </div>
            
            <div className="ai-explain-modal-body">
              {error ? (
                <div className="ai-explain-error">{error}</div>
              ) : isRefreshing ? (
                <div className="ai-explain-loading">
                  <div className="loading-spinner"></div>
                  <p>Generating fresh explanation...</p>
                </div>
              ) : (
                <ReactMarkdown className="ai-explain-content">
                  {explanation || ''}
                </ReactMarkdown>
              )}
            </div>
            
            <div className="ai-explain-modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIExplainButton;